import React, { useState, useEffect } from 'react';
import { Save, Check } from 'lucide-react';
import { Form, Button, Row, Col, InputGroup, Alert } from 'react-bootstrap';
import ImageUpload from './ImageUpload';
import { getMediaUrl, stripMediaUrl } from '../Services/apiService';
import Swal from 'sweetalert2';

const QuestionFormModal = ({ onClose, onSave, prefillData = null }) => {
  
  const [formData, setFormData] = useState({
    text: '',
    type: 'MULTIPLE_CHOICE',
    difficulty: 'EASY',
    correctAnswers: [], // Changed from string to array
    options: ['', '', '', ''],
    timeLimit: 20,
    mediaUrl: '',
    mediaType: 'IMAGE'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
     if (prefillData) {
        console.log('[QuestionFormModal] prefillData received:', prefillData);
        
        // For CLASSIC questions, correctAnswers should be the actual values
        // For MULTIPLE_CHOICE, correctAnswers should be indices
        let answersData = [];
        
        if (prefillData.type === 'CLASSIC') {
            // For classic questions, use correctAnswers directly (they're values, not indices)
            answersData = prefillData.correctAnswers || [];
            console.log('[QuestionFormModal] CLASSIC question, using direct values:', answersData);
        } else if (prefillData.type === 'MULTIPLE_CHOICE') {
            // For multiple choice, find indices of correct answers in options array
            const findIndex = (ans) => {
                return prefillData.options.findIndex(opt => opt === ans);
            };
            
            if (Array.isArray(prefillData.correctAnswers)) {
                answersData = prefillData.correctAnswers.map(findIndex).filter(i => i !== -1);
            } else if (prefillData.correctAnswer) {
                const idx = findIndex(prefillData.correctAnswer);
                if (idx !== -1) answersData = [idx];
            }
            console.log('[QuestionFormModal] MULTIPLE_CHOICE question, converted to indices:', answersData);
        }
        
        setFormData({
            ...prefillData,
            correctAnswers: answersData,
            mediaUrl: prefillData.media ? getMediaUrl(prefillData.media.url) : '',
            mediaType: prefillData.media ? prefillData.media.type : 'IMAGE',
            showMedia: !!prefillData.media
        });
        
        console.log('[QuestionFormModal] formData set with correctAnswers:', answersData);
     }
  }, [prefillData]);

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const toggleCorrectAnswer = (index) => {
      const current = formData.correctAnswers || [];
      const idxPos = current.indexOf(index);
      
      let newAnswers;
      if (idxPos > -1) {
          newAnswers = current.filter(i => i !== index);
      } else {
          newAnswers = [...current, index];
      }
      setFormData({ ...formData, correctAnswers: newAnswers });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[QuestionFormModal] handleSubmit başladı');
    console.log('[QuestionFormModal] formData:', formData);
    
    if (!formData.text || formData.correctAnswers.length === 0) {
        console.log('[QuestionFormModal] Validation failed: text or correctAnswers empty');
        return;
    }

    // Map indices back to values for MULTIPLE_CHOICE, or use directly for others
    let correctAnswerValues;
    if (formData.type === 'MULTIPLE_CHOICE') {
        correctAnswerValues = formData.correctAnswers.map(idx => formData.options[idx]).filter(val => val !== '');
    } else {
        correctAnswerValues = formData.correctAnswers;
        console.log('[QuestionFormModal] CLASSIC question, correctAnswerValues:', correctAnswerValues);
        
        // Validate that classic answers are numeric
        if (formData.type === 'CLASSIC') {
            const answer = correctAnswerValues[0];
            console.log('[QuestionFormModal] Validating numeric answer:', answer);
            if (answer === '' || isNaN(Number(answer))) {
                console.error('[QuestionFormModal] Numeric validation failed!');
                alert('Klasik soru cevabı sayısal bir değer olmalıdır!');
                setIsSubmitting(false);
                return;
            }
            console.log('[QuestionFormModal] Numeric validation passed!');
        }
    }

    if (correctAnswerValues.length === 0) {
        console.log('[QuestionFormModal] correctAnswerValues is empty');
        return;
    }
    console.log('[QuestionFormModal] Final correctAnswerValues:', correctAnswerValues);

    // Prepare payload
    let payload;
    
    // Check if we need FormData (for file upload)
    if (selectedFile && formData.mediaType === 'IMAGE') {
        const data = new FormData();
        data.append('text', formData.text);
        data.append('type', formData.type);
        data.append('difficulty', formData.difficulty);
        data.append('timeLimit', formData.timeLimit);
        // Backend expects stringified JSON for arrays when using FormData
        // For CLASSIC, options should be the single answer so it gets created as an Option
        const optionsPayload = formData.type === 'CLASSIC' ? correctAnswerValues : formData.options;
        data.append('options', JSON.stringify(optionsPayload));
        data.append('correctAnswers', JSON.stringify(correctAnswerValues));
        
        data.append('media', selectedFile);
        
        payload = data;
    } else {
        // Standard JSON payload
        const mediaUrl = formData.mediaType === 'IMAGE' ? stripMediaUrl(formData.mediaUrl) : formData.mediaUrl;
        
        // For CLASSIC, options should be the single answer
        const finalOptions = formData.type === 'CLASSIC' ? correctAnswerValues : formData.options;

        payload = {
            ...formData,
            options: finalOptions,
            correctAnswer: correctAnswerValues.length === 1 ? correctAnswerValues[0] : null,
            correctAnswers: correctAnswerValues,
            // If user cleared the media (showMedia is false OR mediaUrl is empty), ensure we signal removal
            // BUT: if there was media and now mediaUrl is empty, it means remove it.
            // If showMedia is false, we also assume removal if there was pre-existing media.
            removeMedia: (!formData.showMedia || !mediaUrl),
            media: (formData.showMedia && mediaUrl) ? { 
                type: formData.mediaType, 
                url: mediaUrl 
            } : null,
            mediaUrl: (formData.showMedia && mediaUrl) ? mediaUrl : null
        };
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    console.log('[QuestionFormModal] About to call onSave with payload:', payload);
    
    // Wrap in try-catch in case onSave throws, though usually we close anyway
    try {
        if (onSave) {
            console.log('[QuestionFormModal] Calling onSave...');
            await onSave(payload);
            console.log('[QuestionFormModal] onSave completed successfully');
        }
    } catch (error) {
        console.error('[QuestionFormModal] Error saving in modal:', error);
        alert('Soru kaydedilirken hata oluştu: ' + error.message);
        setIsSubmitting(false); // Re-enable if error (though we usually close)
        return; 
    }
    
    console.log('[QuestionFormModal] Closing modal');
    onClose();
  };

  return (
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label className="small fw-bold text-secondary">SORU METNİ</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Sorusunu buraya giriniz..."
            value={formData.text}
            onChange={(e) => setFormData({...formData, text: e.target.value})}
            required
            className="shadow-none"
          />
        </Form.Group>

        <Row className="mb-3">
          <Col md={prefillData ? 12 : 6}>
             <Form.Label className="small fw-bold text-secondary">ZORLUK</Form.Label>
             <Form.Select 
                value={formData.difficulty} 
                onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                className="shadow-none"
             >
                <option value="EASY">Basit</option>
                <option value="MEDIUM">Orta</option>
                <option value="HARD">Zor</option>
             </Form.Select>
          </Col>
          {!prefillData && (
            <Col md={6}>
               <Form.Label className="small fw-bold text-secondary">TİP</Form.Label>
               <Form.Select 
                  value={formData.type} 
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="shadow-none"
               >
                  <option value="MULTIPLE_CHOICE">Çoktan Seçmeli</option>
                  <option value="CLASSIC">Klasik</option>
               </Form.Select>
            </Col>
          )}
        </Row>

        {formData.type === 'MULTIPLE_CHOICE' ? (
          <div className="mb-3">
             <Form.Label className="small fw-bold text-secondary">SEÇENEKLER {formData.correctAnswers.length > 1 && <span className="text-primary small">(Birden fazla doğru cevap seçildi)</span>}</Form.Label>
             <Row className="g-2">
                {formData.options.map((opt, idx) => (
                   <Col md={6} key={idx}>
                          <InputGroup size="sm">
                             <InputGroup.Text className="fw-bold bg-light" style={{width: 30, justifyContent: 'center'}}>
                                {['A', 'B', 'C', 'D'][idx]}
                             </InputGroup.Text>
                             <Form.Control
                                value={opt}
                                onChange={(e) => updateOption(idx, e.target.value)}
                                className={formData.correctAnswers.includes(idx) ? 'border-success bg-success bg-opacity-10' : ''}
                                required
                             />
                             <Button 
                                variant={formData.correctAnswers.includes(idx) ? 'success' : 'outline-secondary'}
                                onClick={() => toggleCorrectAnswer(idx)}
                                title="Doğru Cevap Olarak İşaretle"
                             >
                                <Check size={14} />
                             </Button>
                          </InputGroup>
                   </Col>
                ))}
             </Row>
             {formData.correctAnswers.length === 0 && <div className="text-danger small mt-1">* En az bir doğru cevabı işaretleyiniz.</div>}
          </div>
        ) : (
           <Form.Group className="mb-3">
              <Form.Label className="small fw-bold text-secondary">DOĞRU CEVAP (Sadece Sayı)</Form.Label>
              <Form.Control
                 type="number"
                 step="any"
                 value={formData.correctAnswers[0] || ''}
                 onChange={(e) => setFormData({...formData, correctAnswers: [e.target.value]})}
                 required
                 className="shadow-none"
                 placeholder="Örn: 42 veya 3.14"
              />
           </Form.Group>
        )}

        {/* Media Upload Section */}
        <div className="mb-3 border-top pt-3">
           <Button 
              variant="link" 
              className="p-0 text-decoration-none small fw-bold mb-2" 
              onClick={() => {
                  if (formData.showMedia || formData.mediaUrl) {
                      // Removing media
                      setFormData(prev => ({...prev, showMedia: false, mediaUrl: ''}));
                      setSelectedFile(null);
                  } else {
                      // Adding media
                      setFormData(prev => ({...prev, showMedia: true}));
                  }
              }}
           >
              {formData.showMedia || formData.mediaUrl ? '- Medyayı Kaldır' : '+ Medya Ekle (Sadece Resim)'}
           </Button>
           
           {(formData.showMedia || formData.mediaUrl) && (
              <div className="bg-light p-3 rounded">
                 <Form.Group className="mb-2">
                    <Form.Label className="small fw-bold text-secondary">MEDYA TİPİ</Form.Label>
                     <Form.Select 
                        size="sm"
                        value={formData.mediaType}
                        onChange={(e) => setFormData({...formData, mediaType: e.target.value})}
                        className="mb-2"
                     >
                        <option value="IMAGE">Resim</option>
                     </Form.Select>
                 </Form.Group>

                 {formData.mediaType === 'IMAGE' ? (
                     <ImageUpload 
                        label="RESİM YÜKLE / URL"
                        value={formData.mediaUrl} 
                        onChange={(url) => setFormData({...formData, mediaUrl: url})}
                        onFileChange={setSelectedFile}
                     />
                 ) : (
                     <Form.Group>
                        <Form.Label className="small fw-bold text-secondary">VIDEO URL (Youtube/MP4)</Form.Label>
                        <Form.Control
                           size="sm"
                           placeholder="https://..."
                           value={formData.mediaUrl}
                           onChange={(e) => setFormData({...formData, mediaUrl: e.target.value})}
                        />
                     </Form.Group>
                 )}
              </div>
           )}
        </div>

        <div className="d-flex justify-content-end gap-2 pt-2 border-top">
           <Button variant="light" onClick={onClose}>İptal</Button>
           <Button type="submit" variant="primary">Kaydet ve Ekle</Button>
        </div>
      </Form>
  );
};

export default QuestionFormModal;
